import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { isBlockedEnrollmentPeriodError } from '@/constants/blocked-enrollment-period'
import { REQUEST_TYPE } from '@/constants/request-type'
import { analyzeAcademicTranscript } from '@/services/requests/analyze-transcript'
import { generateAndReturnEquivalencesByRequest } from '@/services/requests/generate-and-return-equivalences-by-request'
import { saveRequestEquivalences } from '@/services/requests/save-request-equivalences'
import { saveRequestPreviousEducation } from '@/services/requests/save-request-previous-education'
import { toastService } from '@/toasts/toastService'
import type { CourseEquivalence } from '@/types/education/course-equivalence'
import type { FileUploadStatus } from '@/types/file-upload/file-upload-status'
import type {
  StudentAutomaticCoursesFormMethods,
  StudentRequestedCoursesFormMethods
} from '@/types/forms/student-requested-courses-form-methods'
import type { Request } from '@/types/requests/request'

export const useSubmitStudentRequestsForm = (
  formMethods: StudentRequestedCoursesFormMethods,
  formMethodsAI: StudentAutomaticCoursesFormMethods
) => {
  const [disabledButtons, setDisabledButtons] = useState(false)
  const [request, setRequest] = useState<Request>()
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
  const [fileStatus, setFileStatus] = useState<FileUploadStatus>('idle')
  const [fileWarning, setFileWarning] = useState<string>()
  const navigate = useNavigate()

  const isSubmittingStep = useRef(false)

  const file = useWatch({
    control: formMethods.control,
    name: 'previousEducation.file'
  })

  function disableButtons(value: boolean) {
    setDisabledButtons(value)
  }

  const { mutate: uploadMutate } = useMutation({
    mutationFn: () => {
      setFileWarning(undefined)
      setFileStatus('pending')
      if (file) {
        return analyzeAcademicTranscript(
          REQUEST_TYPE.CREDIT_TRANSFER.id,
          file,
          request?.id
        )
      }

      throw new Error()
    },
    onSuccess: (data) => {
      if (data.readTranscript) {
        setFileStatus('success')
      } else {
        setFileStatus('warning')
        setFileWarning('Nossa IA não conseguiu ler o documento')
      }
      const request = {
        id: data.id,
        previousEducation: data.previousEducation
      } as Request
      setRequest(request)
    },
    onError: (error) => {
      if (isBlockedEnrollmentPeriodError(error)) {
        toastService.blockedEnrollmentPeriod()
        setFileStatus('idle')
      } else if (error.message.includes('file size')) {
        setFileStatus('error')
        setFileWarning('O arquivo deve ter no máximo 10MB')
      } else {
        setFileWarning('Não foi possível salvar o documento')
        setFileStatus('error')
      }
    }
  })

  const {
    mutate: mutateGenerateEquivalences,
    isPending: isPendingEquivalences
  } = useMutation({
    mutationFn: () => {
      disableButtons(true)
      return generateAndReturnEquivalencesByRequest(request?.id)
    },
    onSuccess: (data) => {
      if (request && formMethods.setValue && formMethodsAI.setValue) {
        const equivalences: CourseEquivalence[] = Array.isArray(
          data?.equivalences
        )
          ? data.equivalences
          : []
        request.courseEquivalences = equivalences
        setRequest(request)

        formMethods.setValue('courseEquivalences', [])
        formMethodsAI.setValue('automaticCourseEquivalences', [])

        equivalences.forEach((equivalence) => {
          if (equivalence.selectedByAI && formMethodsAI.append) {
            formMethodsAI.append(equivalence as any)
          }
          if (!equivalence.selectedByAI && formMethods.append) {
            formMethods.append(equivalence as any)
          }
        })
      }

      disableButtons(false)
    },
    onError: () => {
      disableButtons(false)
      setCurrentStep((step) => Math.max(1, step - 1))
      toastService.requestStepFailed(
        'Não conseguimos gerar as equivalências do seu histórico. Tente novamente em alguns instantes.'
      )
    }
  })

  const {
    mutate: mutatePreviousEducation,
    isPending: isPreviousEducationSavePending
  } = useMutation({
    mutationFn: async () => {
      if (formMethods.getValues) {
        return saveRequestPreviousEducation(
          formMethods.getValues('previousEducation'),
          request?.id
        )
      }

      return undefined
    },
    onSuccess: () => {
      mutateGenerateEquivalences()
      setCurrentStep(2)
    },
    onError: (error) => {
      if (isBlockedEnrollmentPeriodError(error)) {
        toastService.blockedEnrollmentPeriod()
        return
      }

      toastService.requestStepFailed(
        'Não conseguimos salvar os dados do seu curso anterior. Verifique as informações e tente novamente.'
      )
    }
  })

  const {
    mutate: mutateCourseEquivalences,
    isPending: isCourseEquivalencePending,
    isError: isCourseEquivalenceError,
    isSuccess: isCourseEquivalenceSuccess
  } = useMutation({
    mutationFn: async () => {
      if (formMethods.getValues) {
        return saveRequestEquivalences(formMethods.getValues(), request?.id)
      }
    },
    onError: () => {
      disableButtons(false)
      toastService.requestStepFailed(
        'Não conseguimos enviar sua solicitação para análise. Tente novamente em alguns instantes.'
      )
    }
  })

  const handleNext = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isSubmittingStep.current) return
    isSubmittingStep.current = true

    try {
      if (currentStep === 1 && formMethods.trigger) {
        const valid = await formMethods.trigger('previousEducation')
        if (valid && request?.id) {
          mutatePreviousEducation()
        } else {
          window.scrollTo(0, 0)
        }
      }

      if (currentStep === 2 && formMethods.trigger && formMethods.getValues) {
        const valid = await formMethods.trigger('courseEquivalences')
        const manualCourses = formMethods.getValues('courseEquivalences') || []
        const automaticCourses =
          formMethods.getValues('automaticCourseEquivalences') || []
        if (
          valid
          && request?.id
          && (manualCourses.length > 0 || automaticCourses.length > 0)
        ) {
          disableButtons(true)
          mutateCourseEquivalences()
        }
      }
    } finally {
      isSubmittingStep.current = false
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((step) => Math.max(1, step - 1))
    } else {
      navigate(-1)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (request && formMethods.getValues && formMethods.setValue) {
      const previousCourseName = formMethods.getValues(
        'previousEducation.previousCourseName'
      )
      if (
        (previousCourseName?.length || 0) === 0
        && !!request.previousEducation?.name
      ) {
        formMethods.setValue(
          'previousEducation.previousCourseName',
          request.previousEducation.name,
          { shouldTouch: true }
        )
      }

      const previousInstituitionName = formMethods.getValues(
        'previousEducation.previousInstituitionName'
      )
      if (
        (previousInstituitionName?.length || 0) === 0
        && !!request.previousEducation?.institution
      ) {
        formMethods.setValue(
          'previousEducation.previousInstituitionName',
          request.previousEducation.institution,
          {
            shouldTouch: true
          }
        )
      }
    }
  }, [request])

  return {
    states: {
      request,
      isPreviousEducationSavePending,
      isCourseEquivalencePending,
      isCourseEquivalenceError,
      isCourseEquivalenceSuccess,
      currentStep,
      selectedRows,
      fileStatus,
      isPendingEquivalences,
      fileWarning,
      disabledButtons
    },
    actions: {
      setRequest,
      uploadMutate,
      setSelectedRows,
      handleNext,
      handleBack,
      setCurrentStep,
      mutateGenerateEquivalences
    }
  }
}
